import { createAsyncThunk } from "@reduxjs/toolkit";
import { get, post } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { normalizeAuditLogApiItem } from "../../utils/normalizeAuditLog";
import { extractAuditLogListPayload } from "../../utils/extractAuditLogListPayload";
import { monthLockRowFromListItem } from "../../utils/monthLockApi";

/** Only include clientId in JSON body when a real client is selected (not Global). */
function hasClientIdForRequest(clientId) {
  if (clientId == null) return false;
  const s = String(clientId).trim();
  return s.length > 0;
}

/**
 * POST /month-locks — create or schedule a month lock.
 * @param {{ month: string, useAutoLock: boolean, clientId?: string, clientLabel: string }} payload
 */
export const createMonthLock = createAsyncThunk(
  "reports/createMonthLock",
  async (payload, { rejectWithValue }) => {
    try {
      const body = {
        month: payload.month,
        useAutoLock: Boolean(payload.useAutoLock),
      };
      if (hasClientIdForRequest(payload.clientId)) {
        body.clientId = String(payload.clientId).trim();
      }
      const response = await post(API_ENDPOINTS.REPORTS.MONTH_LOCKS, body, {}, "json", {
        suppressGlobalErrorModal: true,
      });
      const raw = response?.data;
      return {
        body: raw,
        meta: {
          month: payload.month,
          clientLabel: payload.clientLabel,
        },
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create month lock"));
    }
  }
);

/**
 * POST /month-locks/:lockId/unlock — body: { reason: string }
 */
export const unlockMonthLock = createAsyncThunk(
  "reports/unlockMonthLock",
  async ({ lockId, reason }, { rejectWithValue }) => {
    try {
      const id = String(lockId ?? "").trim();
      if (!id) {
        return rejectWithValue("Missing month lock id");
      }
      const body = {
        reason: reason != null && String(reason).trim() !== "" ? String(reason).trim() : "",
      };
      const response = await post(API_ENDPOINTS.REPORTS.MONTH_LOCK_UNLOCK(id), body, {}, "json", {
        suppressGlobalErrorModal: true,
      });
      return {
        body: response?.data,
        meta: { lockId: id },
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to unlock month"));
    }
  }
);

/**
 * GET /month-locks — list month lock rows (hydrates table after refresh).
 * Query: page, limit, month (YYYY-MM), client_id (uuid — omit for Global / all clients)
 */
export const fetchMonthLocks = createAsyncThunk(
  "reports/fetchMonthLocks",
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = {};
      if (params.page != null) query.page = params.page;
      if (params.limit != null) query.limit = params.limit;
      if (params.month) query.month = params.month;
      if (params.client_id) query.client_id = params.client_id;

      const response = await get(API_ENDPOINTS.REPORTS.MONTH_LOCKS, query);
      const body = response?.data;
      const { rows } = extractAuditLogListPayload(body);
      const items = rows.map(monthLockRowFromListItem).filter(Boolean);

      return { items };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch month locks"));
    }
  }
);

/**
 * Fetch paginated audit log (GET /audit-logs).
 * Query: action, user_id, date_from, date_to, page, limit
 */
export const fetchAuditLogs = createAsyncThunk(
  "reports/fetchAuditLogs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = {};
      if (params.action) query.action = params.action;
      if (params.user_id) query.user_id = params.user_id;
      if (params.date_from) query.date_from = params.date_from;
      if (params.date_to) query.date_to = params.date_to;
      if (params.page != null) query.page = params.page;
      if (params.limit != null) query.limit = params.limit;

      const response = await get(API_ENDPOINTS.REPORTS.AUDIT_LOGS, query);
      const body = response?.data;
      const { rows, total, page, limit } = extractAuditLogListPayload(body);

      return {
        items: rows.map(normalizeAuditLogApiItem),
        total,
        page,
        limit,
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch audit logs"));
    }
  }
);
